import React from 'react';
import { get, find, map, filter, cloneDeep, isUndefined, isEmpty } from 'lodash';

// React view component to render
import { memberSavedAddress as MemberSavedAddressView } from 'ui/views/widgets/memberSavedAddress';
import { MemberSavedAddressService } from 'services/memberSavedAddress';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, GLOBAL_LABELS, SIZES, BLANK_VALUE_SYMBOL } from 'constants/common';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberSavedAddress } from 'ui/containers/MemberSavedAddress';
 *
 * // render
 * <MemberSavedAddress propName='propValue'/>
 */
export class MemberSavedAddress extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind function
        this._bind();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined
        };

        // Member Detail instance
        this.memberSavedAddressService = new MemberSavedAddressService();

        // start member detail data fetch
        this.fetchComponentData();
    }

    _bind() {
        this.handleAddressInfoUpdate = this.handleAddressInfoUpdate.bind( this );
        this.updateAddressSelection = this.updateAddressSelection.bind( this );
        this.handleSetDefaultAction = this.handleSetDefaultAction.bind( this );
        this.handleDeleteAction = this.handleDeleteAction.bind( this );
        this.handleInactivateAction = this.handleInactivateAction.bind( this );
        this.handleActivateAction = this.handleActivateAction.bind( this );
    }

    /**
     * @desc: fetch component data
     */
    async fetchComponentData() {
        log.debug( 'MemberSavedAddress.fetchComponentData()' );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();
       

        // fetch component data
        const { status, data } = await this.memberSavedAddressService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: get( this.props, 'requestPayload.context', {} )
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            // get the address list
            const addressList = get( data, 'addresses', [] );

            // get selected address
            const selectedAddress = find( addressList, { selected: true } );

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            }, () => {

                // call update function to inform parent container
                this.props.updateCheckoutSelection( { type: 'address', item: selectedAddress } );
            } );
         this.props.updateZip(this.state.componentData.addresses.find(val => val.selected).zip );
        }  

    }

    /**
     * @desc: called when user updates the currect address selection
     */
    updateAddressSelection( { address = {} } ) {
        log.debug( 'MemberSavedAddress.updateAddressSelection()' );

        const componentData = cloneDeep( this.state.componentData );
        const updatedList = map( componentData.addresses, item => {
            item.selected = item.id === address.id ? true: false;
            return item;
        } );
        componentData.addresses = updatedList;

        this.setState( { componentData }, () => {
            this.props.updateCheckoutSelection( { type: 'address', item: address } );
        } );
        console.log( address.zip);
        this.props.updateZip( address.zip );
    }

    /**
     * @desc: called when user updates the currect address selection
     */
    async handleSetDefaultAction( { address } ) {
        log.debug( 'MemberSavedAddress.handleSetDefaultAction()' );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // set the address default
        const { status, data } = await this.memberSavedAddressService.setDefaultAddress( {
            url,
            data: {
                context: {
                    ...get( this.props, 'requestPayload.context', {} ),
                },
                data: {
                    id: address.id
                }
            }
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.addresses, item => {
                item.flags.isDefault = address.id === item.id ? true: false;
                return item;
            } );
            componentData.addresses = updatedList;
    
            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isFetching: true
            }, () => {

                // Fetch component data again to sync
                // inactivate flags from impl: 40683
                this.fetchComponentData();
                
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );


        } else {

            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc: action executed when MSR clicks on delete action
     */
    async handleDeleteAction( { address } ) {
        log.debug( 'MemberSavedAddress.handleDeleteAction()' );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();

        // get delete action
        const action = find( this.state.componentData.modifyActions, { name: 'delete' } );

        const isDefault = get( address, 'flags.isDefault', false );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation.message', {} ) ),
                iconName: 'close',
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                }
            } );
            
            if( shouldProceed ) {
    
                //show preloader
                pageLevelPreloader.show();
    
                // set the address default
                const { status, data } = await this.memberSavedAddressService.deleteSavedAddress( {
                    url: action.url,
                    data: {
                        context: {
                            ...get( this.props, 'requestPayload.context', {} ),
                            operation: CRUD_OPERATION.DELETE
                        },
                        data: {
                            id: address.id
                        }
                    }
                } );
    
                //show preloader
                pageLevelPreloader.hide();
    
                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
    
                    // update address list
                    const componentData = cloneDeep( this.state.componentData );
                    const updatedList = filter( componentData.addresses, item => {
                        return item.id !== address.id;
                    } );
                    componentData.addresses = updatedList;
    
                    this.setState( {
                        componentData,
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
                    } );
    
                } else {
                
                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    } else {
                        errorOverlay.open();
                    }

                }
            }
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * @desc: action executed when MSR clicks on Inactivate action
     */
    async handleInactivateAction( { address, selected } ) {
        log.debug( 'MemberSavedAddress.handleInactivateAction()' );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();

        // get delete action
        const action = find( this.state.componentData.modifyActions, { name: 'inactivate' } );

        const isDefault = get( address, 'flags.isDefault', false );

        if( ! isDefault ) {

            const addressOption = get( this.state, 'componentData.options.addressType', [] );
            const type = find( addressOption, { value: address.type } );

            const shouldProceed = await confirmOverlay.open( {
                message: getLabel( get( action, 'confirmation.message', {} ) ),
                iconName: 'close',
                confirmViewConfig: {
                    type: 'address',
                    address: {
                        type: isUndefined( type ) ? BLANK_VALUE_SYMBOL: getLabel( type.title ),
                        value: filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' )
                    }
                }
            } );
            
            if( shouldProceed ) {
    
                //show preloader
                pageLevelPreloader.show();
    
                // set the address default
                const { status, data } = await this.memberSavedAddressService.inactivateSavedAddress( {
                    url: action.url,
                    data: {
                        context: {
                            ...get( this.props, 'requestPayload.context', {} )
                        },
                        data: {
                            id: address.id,
                            type: address.type,
                            line1: address.line1,
                            line2: address.line2,
                            city: address.city,
                            state: address.state,
                            zip: address.zip
                        }
                    }
                } );
    
                //show preloader
                pageLevelPreloader.hide();
    
                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
    
                    // update address
                    const componentData = cloneDeep( this.state.componentData );
                    const updatedItem = find( componentData.addresses, { id: address.id  } );
           
                    updatedItem.flags.isActivated = false;
    
                    this.setState( {
                        componentData,
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
                    }, () => {
                        selected ? this.updateAddressSelection( {} ) : null;
                    } );
    
                } else {
                
                    // show error dialog
                    if( ! isEmpty( get( data, 'description' ) ) ) {
                        errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                    } else {
                        errorOverlay.open();
                    }

                }
            }
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * @desc: action executed when MSR clicks on Activate action
     */
    async handleActivateAction( { address } ) {
        log.debug( 'MemberSavedAddress.handleActivateAction()' );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();

        // get delete action
        const action = find( this.state.componentData.modifyActions, { name: 'activate' } );

        const isDefault = get( address, 'flags.isDefault', false );

        if( ! isDefault ) {

            //show preloader
            pageLevelPreloader.show();

            // set the address default
            const { status, data } = await this.memberSavedAddressService.activateSavedAddress( {
                url: action.url,
                data: {
                    context: {
                        ...get( this.props, 'requestPayload.context', {} )
                    },
                    data: {
                        id: address.id,
                        type: address.type,
                        line1: address.line1,
                        line2: address.line2,
                        city: address.city,
                        state: address.state,
                        zip: address.zip
                    }
                }
            } );

            //show preloader
            pageLevelPreloader.hide();

            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                // update address list
                const componentData = cloneDeep( this.state.componentData );
                const updatedItem = find( componentData.addresses, { id: address.id  } );
            
                updatedItem.flags.isActivated = true;

                this.setState( {
                    componentData,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
                } );

            } else {
            
                // show error dialog
                if( ! isEmpty( get( data, 'description' ) ) ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                } else {
                    errorOverlay.open();
                }

            }
        } else {

            errorOverlay.open( {
                message: getLabel( action.prohibit ),
                size: 'small',
                setDangerously: true
            } );
        }
    }

    /**
     * add/edit opration handler
     */
    async handleAddressInfoUpdate( { type, address, id = null } ) {
        log.debug( 'MemberSavedAddress.handleAddressInfoUpdate()' );

        // get action
        const action = get( this.state, 'componentData.addAction', {} );

        // cancel ongoing request
        this.memberSavedAddressService.cancel();
        
        // show page preloader
        pageLevelPreloader.show();

        const formData = {
            ...address,
            id: id,
        };

        if( 'edit' === type ) {
            const selectedAddress = find( this.state.componentData.addresses, { id: id } );
            formData.isDefault = ! isUndefined( selectedAddress ) ? get( selectedAddress, 'flags.isDefault' ): null;
        }

         // set the address default
        const { status, data } = await this.memberSavedAddressService.submitSelectedAddress( {
            url: get( action, 'config.submitUrl', null ),
            data: {
                context: {
                    ...get( this.props, 'requestPayload.context', {} ),
                    operation: 'add' === type ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE
                },
                data: formData
            }
        } );

        // show page preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                isFetching: true
            }, () => {

                // fetch new list
                this.fetchComponentData();

            } );

        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }

    }


    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
    */
    componentWillUnmount() {
        this.memberSavedAddressService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberSavedAddressView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} )}
                requestPayload={ this.props.requestPayload }
                onAddressSelection={ this.updateAddressSelection }
                onDefaultSelection={ this.handleSetDefaultAction }
                onAddressActivate={ this.handleActivateAction }
                onAddressInactivate={ this.handleInactivateAction }
                onAddressDelete={ this.handleDeleteAction }
                onAddEditAddressClick={ this.handleAddressInfoUpdate }
            />
        );
    }
}

// set display name
MemberSavedAddress.displayName = 'MemberSavedAddress';

// set default props
MemberSavedAddress.defaultProps = {};

// prop types
MemberSavedAddress.propTypes = {};
